import { useMemo, useRef } from "react"
import { Channel, DefaultGenerics, StreamChat } from "stream-chat"

import { useFlag } from "../../../feature-flags/FeatureFlagProvider"
import { MESSAGE_REQUESTS_FLAG_NAME } from "../../../feature-flags/flagNames"
import { useAllChannels } from "../../../hooks/useAllChannels"
import {
  UserMessageChannelData,
  useUserMessageChannels,
} from "../../../hooks/useUserMessageChannels"

const isHankChat = (channel: Channel<DefaultGenerics>): boolean => {
  const memberIds: string[] = channel?.state?.members
    ? Object.keys(channel?.state?.members)
    : []

  return memberIds.includes("hello-gethank-com") || memberIds.includes("Hank")
}

/**
 * Lazy-loaded channel lists hook.
 * This hook fetches and computes channel lists (mainInbox, messageRequests)
 * only when explicitly enabled. Use this on the messenger page where the
 * data is actually needed, not in the global provider.
 */
export const useLazyChannelLists = (
  client: StreamChat<DefaultGenerics> | undefined,
  enabled = true,
) => {
  const flag = useFlag(MESSAGE_REQUESTS_FLAG_NAME)

  const {
    channels: streamChannels,
    isLoading: isLoadingAllChannels,
    error: streamError,
  } = useAllChannels(client)

  // Stabilize channel IDs to prevent unnecessary re-fetches
  // Only update when the actual IDs change, not on every render
  const prevChannelIdsRef = useRef<string[]>([])

  const { groupChannelIds, otherChannelIds } = useMemo(() => {
    if (!enabled || !streamChannels) {
      return { groupChannelIds: [], otherChannelIds: [] }
    }

    const group =
      streamChannels
        .filter((channel) => isHankChat(channel))
        .map((channel) => channel?.id)
        .filter(Boolean) || []

    const other =
      streamChannels
        .filter((channel) => !isHankChat(channel))
        .map((channel) => channel?.id)
        .filter(Boolean) || []

    return { groupChannelIds: group, otherChannelIds: other }
  }, [enabled, streamChannels])

  // Stabilize the IDs array - only update ref if content actually changed
  const stableChannelIds = useMemo(() => {
    const newIds = otherChannelIds
    const prevIds = prevChannelIdsRef.current

    // Check if arrays are equal
    const areEqual =
      newIds.length === prevIds.length &&
      newIds.every((id, i) => id === prevIds[i])

    if (!areEqual) {
      prevChannelIdsRef.current = newIds
      return newIds
    }

    return prevIds
  }, [otherChannelIds])

  const {
    messageChannels,
    isLoading: isLoadingMessageChannels,
    error: userMessageChannelsError,
  } = useUserMessageChannels(enabled ? stableChannelIds : null)

  const validMessageChannels = useMemo(
    () => messageChannels?.filter(Boolean) ?? [],
    [messageChannels],
  )

  const showInMainInbox = useMemo(() => {
    return (channel: UserMessageChannelData) => {
      const streamChannelFound = streamChannels?.find(
        ({ id }) => id === channel.message_channel.stream_channel_id,
      )

      if (!streamChannelFound) {
        return false
      }

      // Group channels always show in the main inbox, even without messages.
      // This ensures welcome/onboarding group chats are visible for new users.
      if (channel.message_channel.is_group_message) {
        return true
      }

      const channelData = streamChannelFound.data as { last_message_at?: string }
      const hasMessages = !!channelData?.last_message_at

      if (!hasMessages) {
        return false
      }

      return flag
        ? channel.shared_experience_title || channel.is_accepted
        : true
    }
  }, [streamChannels, flag])

  const mainInboxChannelIds: string[] = useMemo(() => {
    if (!enabled) return []
    return [
      ...groupChannelIds,
      ...validMessageChannels
        .filter(showInMainInbox)
        .map((channel) => channel.message_channel.stream_channel_id),
    ]
  }, [enabled, groupChannelIds, validMessageChannels, showInMainInbox])

  const messageRequestsChannelIds: string[] = useMemo(() => {
    if (!enabled) return []
    return validMessageChannels
      .filter((channel) => !showInMainInbox(channel))
      .map((channel) => channel.message_channel.stream_channel_id)
  }, [enabled, validMessageChannels, showInMainInbox])

  // Only consider useUserMessageChannels loading when there are actually
  // non-Hank channel IDs to fetch. React Query v4 reports isLoading=true
  // for disabled queries with no cache, which would cause a permanent
  // spinner for new users who only have the Hank welcome channel.
  const hasChannelsToFetch = enabled && stableChannelIds.length > 0

  // Loading is true only while the underlying queries are actively fetching.
  // Previously, we also checked for empty/undefined results, which caused an
  // infinite loading state for new users whose UserMessageChannel records
  // don't exist yet (e.g., when the member.added webhook fails).
  const isLoading = useMemo(() => {
    if (!enabled) return false
    return isLoadingAllChannels || (hasChannelsToFetch && isLoadingMessageChannels)
  }, [enabled, isLoadingAllChannels, hasChannelsToFetch, isLoadingMessageChannels])

  return {
    mainInboxChannelIds,
    messageRequestsChannelIds,
    isLoading,
    error: streamError || userMessageChannelsError,
  }
}
